import RoundedButton from 'components/RoundedButton';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { ENTER_CTA_LINK } from 'utils/cta/CTA';
import AboutUsModule from './AboutUsModule';
import AboutUsSiteContainer from './AboutUsSiteContainer';
import styles from './AboutUsTextAndCarousel.module.css';

interface CarouselItem {
  title: string;
  text: string;
  imagePath: string;
  imageAlt: string;
}

interface Props {
  children: ReactNode;
  items: CarouselItem[];
  background: Parameters<typeof AboutUsModule>[0]['background'];
}

const AboutUsTextAndCarousel = (props: Props) => {
  const [pipCount, setPipCount] = useState<number | undefined>(undefined);
  const [selectedPip, setSelectedPip] = useState(0);
  const scrollContainer = useRef<HTMLDivElement>(null);

  const changeSelectedIndex = (delta: number) => {
    onPipSelected(selectedPip + delta);
  };

  const onPipSelected = (index: number) => {
    if (!scrollContainer.current) {
      return;
    }

    const item = scrollContainer.current.childNodes[0].childNodes[index] as
      | Element
      | undefined;
    if (!item) {
      return;
    }
    const leftOfItem =
      item.getBoundingClientRect().left -
      scrollContainer.current.getBoundingClientRect().left +
      scrollContainer.current.scrollLeft;

    scrollContainer.current.scrollTo({
      left: leftOfItem,
      top: 0,
      behavior: 'smooth',
    });
  };

  const updateSelectedItemBasedOnScrollPosition = () => {
    if (!scrollContainer.current) {
      return;
    }

    const secondItem = scrollContainer.current.childNodes[0].childNodes[1] as
      | Element
      | undefined;
    if (!secondItem) {
      return;
    }

    const scrollPosition = scrollContainer.current.scrollLeft;
    const itemWidth =
      secondItem.getBoundingClientRect().left -
      scrollContainer.current.getBoundingClientRect().left +
      scrollContainer.current.scrollLeft;

    setSelectedPip(
      Math.min((pipCount || 0) - 1, Math.ceil(scrollPosition / itemWidth)),
    );
  };

  const updateVisibleCount = () => {
    if (!scrollContainer.current) {
      return;
    }

    const secondItem = scrollContainer.current.childNodes[0].childNodes[1] as
      | Element
      | undefined;
    if (!secondItem) {
      return;
    }

    const itemWidth =
      secondItem.getBoundingClientRect().left -
      scrollContainer.current.getBoundingClientRect().left +
      scrollContainer.current.scrollLeft;

    let visibleItemCount = scrollContainer.current.clientWidth / itemWidth;

    // Adjust the visible number of pips if we're almost showing all of a carousel item
    const roundedDown = Math.floor(visibleItemCount);
    const roundedUp = Math.ceil(visibleItemCount);
    visibleItemCount =
      Math.abs(visibleItemCount - roundedDown) <= 0.2
        ? roundedUp
        : Math.round(visibleItemCount);

    setPipCount(props.items.length - Math.floor(visibleItemCount) + 1);
    updateSelectedItemBasedOnScrollPosition();
  };

  useEffect(() => {
    if (!scrollContainer.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => updateVisibleCount());
    resizeObserver.observe(scrollContainer.current as Element);

    scrollContainer.current.addEventListener(
      'scroll',
      () => {
        updateSelectedItemBasedOnScrollPosition();
      },
      { passive: true },
    );

    updateVisibleCount(); // Initial measurement

    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollContainer.current]);

  return (
    <AboutUsModule background={'tealGradient'}>
      <AboutUsSiteContainer>
        <div className={styles.container}>
          {props.children}
          <div className={styles.carouselButtonContainer}>
            <button
              className={
                styles.carouselButton + ' ' + styles.carouselButtonPrevious
              }
              onClick={() => changeSelectedIndex(-1)}
            >
              <svg width="11" height="24" viewBox="0 0 11 24" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.73021 12.3435C6.85507 11.2839 6.01403 10.2663 5.17352 9.24793C3.63085 7.37882 2.08832 5.5095 0.546171 3.6397C0.179329 3.19494 0.0531962 2.66477 0.203234 2.06101C0.348412 1.47714 0.684826 1.12056 1.17495 1.00135C1.63155 0.890251 2.02186 1.06767 2.34934 1.46425C3.43305 2.77682 4.51873 4.08689 5.60205 5.39989C7.17541 7.30677 8.74746 9.21541 10.3199 11.1234C10.9445 11.8812 10.9465 12.7658 10.3209 13.5242C7.66983 16.7386 5.01902 19.9532 2.36514 23.1641C1.63155 24.0518 0.468372 23.7302 0.196054 22.5639C0.0604638 21.983 0.174207 21.4625 0.530979 21.0302C2.85772 18.2106 5.18451 15.3911 7.51213 12.5724C7.5736 12.498 7.64357 12.4339 7.73021 12.3435Z"
                  fill="white"
                />
              </svg>
            </button>
            <div className={styles.carouselContainer}>
              <div
                className={styles.carouselScrollContainer}
                ref={scrollContainer}
              >
                <div className={styles.carouselTrack}>
                  {props.items.map(carouselCard)}
                </div>
              </div>
              {pipCount != undefined ? (
                <Pips
                  pipCount={pipCount}
                  selectedPip={selectedPip}
                  onPipSelected={onPipSelected}
                />
              ) : undefined}
            </div>
            <button
              className={
                styles.carouselButton + ' ' + styles.carouselButtonNext
              }
              onClick={() => changeSelectedIndex(1)}
            >
              <svg width="11" height="24" viewBox="0 0 11 24" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.73021 12.3435C6.85507 11.2839 6.01403 10.2663 5.17352 9.24793C3.63085 7.37882 2.08832 5.5095 0.546171 3.6397C0.179329 3.19494 0.0531962 2.66477 0.203234 2.06101C0.348412 1.47714 0.684826 1.12056 1.17495 1.00135C1.63155 0.890251 2.02186 1.06767 2.34934 1.46425C3.43305 2.77682 4.51873 4.08689 5.60205 5.39989C7.17541 7.30677 8.74746 9.21541 10.3199 11.1234C10.9445 11.8812 10.9465 12.7658 10.3209 13.5242C7.66983 16.7386 5.01902 19.9532 2.36514 23.1641C1.63155 24.0518 0.468372 23.7302 0.196054 22.5639C0.0604638 21.983 0.174207 21.4625 0.530979 21.0302C2.85772 18.2106 5.18451 15.3911 7.51213 12.5724C7.5736 12.498 7.64357 12.4339 7.73021 12.3435Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
          <div className={styles.buttonContainer}>
            <RoundedButton href={ENTER_CTA_LINK} rebrandDm2025>
              Enter draw
            </RoundedButton>
          </div>
        </div>
      </AboutUsSiteContainer>
    </AboutUsModule>
  );
};

const carouselCard = (props: CarouselItem, index: number) => (
  <div key={index} className={styles.carouselItem}>
    <img src={props.imagePath} alt={props.imageAlt} />
    <div className={styles.carouselItemText}>
      <strong>{props.title}</strong>
      <p>{props.text}</p>
    </div>
  </div>
);

interface PipsProps {
  pipCount: number;
  selectedPip: number;
  onPipSelected: (index: number) => void;
}
const Pips = (props: PipsProps) => {
  return (
    <div className={styles.pipContainer}>
      {Array.from({ length: props.pipCount }, (_, i) => (
        <button
          className={
            styles.pip +
            ' ' +
            (props.selectedPip == i ? styles.pipSelected : '')
          }
          key={i}
          onClick={() => props.onPipSelected(i)}
        >
          <span className="visuallyHidden">{i}</span>
        </button>
      ))}
    </div>
  );
};

export default AboutUsTextAndCarousel;
